// src/lib/vouchers.ts
import { supabase } from '../supabaseClient';
import { getVoucherStatusLabel, getLanguageCode } from './transactionI18n';

export type VoucherScanSource = 'manual' | 'camera' | 'edge_function' | 'unknown' | string;

export type VoucherRedemptionInfo = {
  business_id?: string | null;
  business_name?: string | null;
  redeemed_by?: string | null;
  redeemed_at?: string | null;
  source?: VoucherScanSource | null;
  log_id?: string | null;
  note?: string | null;
};

export type ShopVoucher = {
  id: string;
  order_id?: string | null;
  order_item_id?: string | null;
  user_id?: string | null;
  customer_email?: string | null;
  business_id?: string | null;
  voucher_code: string;
  secure_token?: string | null;
  status: 'active' | 'redeemed' | 'cancelled' | 'expired' | string;
  effective_status?: 'active' | 'redeemed' | 'cancelled' | 'expired' | string;
  title?: string | null;
  amount_cents?: number | null;
  currency?: string | null;
  redeemed_at?: string | null;
  redeemed_by?: string | null;
  expires_at?: string | null;
  metadata?: any;
  order?: any;
  item?: any;
  business?: { id?: string | null; name?: string | null } | null;
  redemption_log?: any;
  created_at?: string | null;
  updated_at?: string | null;
};

export type VoucherLookupPayload = {
  token?: string;
  code?: string;
};

export type MerchantVoucherResponse = {
  ok: boolean;
  voucher?: ShopVoucher | null;
  business?: { id?: string | null; name?: string | null } | null;
  redemption_log?: any;
  error?: string;
  message?: string;
};

export const getVoucherUrl = (token?: string | null) => `/voucher/${encodeURIComponent(String(token || ''))}`;

export const getVoucherQrUrl = (token: string) => {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://forfeoexplore.com';
  const url = `${base}${getVoucherUrl(token)}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(url)}&color=061120&bgcolor=ffffff`;
};

export const getVoucherEffectiveStatus = (voucherOrStatus?: ShopVoucher | string | null) => {
  if (!voucherOrStatus) return 'active';
  if (typeof voucherOrStatus === 'string') return voucherOrStatus.toLowerCase();

  const explicit = String(voucherOrStatus.effective_status || voucherOrStatus.status || 'active').toLowerCase();
  if (explicit === 'active' && voucherOrStatus.expires_at) {
    const ts = Date.parse(voucherOrStatus.expires_at);
    if (Number.isFinite(ts) && ts < Date.now()) return 'expired';
  }
  return explicit;
};

export const displayVoucherStatus = (status?: string | null, language?: string) => getVoucherStatusLabel(status, language);

export const getVoucherStatusBadgeClass = (status?: string | null) => {
  const s = String(status || 'active').toLowerCase();
  if (s === 'active') return 'bg-emerald-50 text-emerald-700 border border-emerald-100';
  if (s === 'redeemed') return 'bg-slate-100 text-slate-700 border border-slate-200';
  if (s === 'expired') return 'bg-orange-50 text-orange-700 border border-orange-100';
  if (s === 'cancelled' || s === 'canceled') return 'bg-red-50 text-red-700 border border-red-100';
  return 'bg-blue-50 text-blue-700 border border-blue-100';
};

export const getVoucherStatusCardClass = (status?: string | null) => {
  const s = String(status || 'active').toLowerCase();
  if (s === 'active') return 'border-blue-100 bg-blue-50/60';
  if (s === 'redeemed') return 'border-slate-200 bg-slate-50';
  if (s === 'expired') return 'border-orange-100 bg-orange-50/70';
  if (s === 'cancelled' || s === 'canceled') return 'border-red-100 bg-red-50/70';
  return 'border-blue-100 bg-blue-50/60';
};

export const getVoucherRedemptionInfo = (voucher?: ShopVoucher | null): VoucherRedemptionInfo | null => {
  if (!voucher) return null;
  const meta = voucher.metadata || {};
  const redemption = meta.redemption || meta.redeemed || null;
  if (redemption && typeof redemption === 'object') return redemption as VoucherRedemptionInfo;
  if (voucher.redeemed_at || voucher.redeemed_by) {
    return {
      redeemed_at: voucher.redeemed_at,
      redeemed_by: voucher.redeemed_by,
      business_id: voucher.business_id,
      business_name: voucher.business?.name || null,
      source: 'unknown',
    };
  }
  return null;
};

export const formatVoucherAmount = (amountCents?: number | null, currency = 'cad', language?: string) => {
  const amount = Number(amountCents || 0) / 100;
  const locale = getLanguageCode(language) === 'en' ? 'en-CA' : 'fr-CA';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: String(currency || 'cad').toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} $`;
  }
};

export const extractVoucherTokenOrCode = (rawInput: string): VoucherLookupPayload => {
  const raw = String(rawInput || '').trim();
  if (!raw) return {};

  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }

  const normalized = decoded.trim();

  const codeMatch = normalized.match(/\bFE-[A-Z0-9]{6,32}\b/i);
  if (codeMatch?.[0]) {
    return { code: codeMatch[0].toUpperCase() };
  }

  try {
    const url = new URL(normalized);
    const parts = url.pathname.split('/').filter(Boolean);
    const voucherIndex = parts.findIndex((part) => part.toLowerCase() === 'voucher');
    if (voucherIndex >= 0 && parts[voucherIndex + 1]) {
      return { token: parts[voucherIndex + 1].trim() };
    }

    const tokenFromQuery = url.searchParams.get('token') || url.searchParams.get('voucher') || url.searchParams.get('secure_token');
    if (tokenFromQuery) return { token: tokenFromQuery.trim() };
  } catch {
    // L'entrée n'est pas une URL complète. On teste quand même le chemin.
  }

  const pathMatch = normalized.match(/\/voucher\/([^\s?#/]+)/i);
  if (pathMatch?.[1]) {
    return { token: pathMatch[1].trim() };
  }

  // Les tokens sécurisés générés par Forfeo ne contiennent généralement pas d'espaces.
  if (/^[a-zA-Z0-9_-]{24,160}$/.test(normalized)) {
    return { token: normalized };
  }

  return { code: normalized.toUpperCase() };
};

export const fetchVouchersForOrders = async (orderIds: string[]) => {
  const ids = orderIds.filter(Boolean);
  if (ids.length === 0) return new Map<string, ShopVoucher[]>();

  const { data, error } = await supabase
    .from('shop_order_vouchers')
    .select('*')
    .in('order_id', ids)
    .order('created_at', { ascending: true });

  if (error) {
    console.warn('fetchVouchersForOrders error:', error.message);
    return new Map<string, ShopVoucher[]>();
  }

  const map = new Map<string, ShopVoucher[]>();
  (data || []).forEach((voucher: any) => {
    const key = String(voucher.order_id || '');
    if (!key) return;
    const list = map.get(key) || [];
    list.push(voucher as ShopVoucher);
    map.set(key, list);
  });

  return map;
};

export const fetchVoucherByToken = async (token: string) => {
  const cleaned = String(token || '').trim();

  if (!cleaned) {
    return { data: null, error: new Error('Token manquant') };
  }

  const { data, error } = await supabase
    .from('shop_order_vouchers')
    .select('*, order:shop_orders(*), item:shop_order_items(*)')
    .eq('secure_token', cleaned)
    .maybeSingle();

  return { data: data as any, error };
};

const getMerchantFunctionHeaders = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error(`Impossible de vérifier la session commerçant : ${error.message}`);
  const accessToken = session?.access_token?.trim();
  if (!accessToken) throw new Error('Session commerçant expirée. Reconnectez-vous avant de scanner un pass.');
  return { Authorization: `Bearer ${accessToken}` };
};

export const validateMerchantVoucher = async (
  input: string,
  businessId: string,
  source: VoucherScanSource = 'manual'
): Promise<MerchantVoucherResponse> => {
  const lookup = extractVoucherTokenOrCode(input);
  if (!businessId) throw new Error('Entreprise introuvable.');
  if (!lookup.token && !lookup.code) throw new Error('Code voucher manquant.');

  const headers = await getMerchantFunctionHeaders();

  try {
    const { data, error } = await supabase.functions.invoke('validate-shop-voucher', {
      headers,
      body: {
        ...lookup,
        business_id: businessId,
        source,
      },
    });

    if (error) throw error;
    return data as MerchantVoucherResponse;
  } catch (err: any) {
    // Supabase throw une erreur générique, mais le JSON est dans err.context?.response
    if (err?.context?.response) {
      try {
        const errorJson = await err.context.response.json();
        throw new Error(errorJson.error || 'unknown_error');
      } catch {
        throw err;
      }
    }
    throw err;
  }
};

export const redeemMerchantVoucher = async (
  input: string,
  businessId: string,
  options?: { source?: VoucherScanSource; note?: string }
): Promise<MerchantVoucherResponse> => {
  const lookup = extractVoucherTokenOrCode(input);
  if (!businessId) throw new Error('Entreprise introuvable.');
  if (!lookup.token && !lookup.code) throw new Error('Code voucher manquant.');

  const headers = await getMerchantFunctionHeaders();

  const { data, error } = await supabase.functions.invoke('redeem-shop-voucher', {
    headers,
    body: {
      ...lookup,
      business_id: businessId,
      source: options?.source || 'manual',
      note: options?.note || null,
    },
  });

  if (error) throw error;
  return data as MerchantVoucherResponse;
};
